import { Component, ElementRef, HostListener, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { GlobalSearchService, GlobalSearchGroup, GlobalSearchResultItem } from '../../../services/global-search.service';

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss']
})
export class GlobalSearchComponent implements OnDestroy {
  query = '';
  loading = false;
  isOpen = false;
  totalHits = 0;
  groups: GlobalSearchGroup[] = [];
  activeIndex = -1;

  private readonly searchQuery$ = new Subject<string>();
  private readonly subscriptions = new Subscription();

  constructor(
    private globalSearchService: GlobalSearchService,
    private router: Router,
    private elementRef: ElementRef<HTMLElement>,
    private translateService: TranslateService
  ) {
    const sub = this.searchQuery$
      .pipe(
        debounceTime(220),
        distinctUntilChanged(),
        switchMap((value) => {
          if (value.length < 2) {
            this.resetResults();
            return of(null);
          }

          this.loading = true;
          return this.globalSearchService.search(value).pipe(
            catchError(() => of(null))
          );
        })
      )
      .subscribe((response) => {
        if (response == null) {
          this.loading = false;
          return;
        }

        this.loading = false;

        if (response?.success && response.result) {
          this.groups = response.result.groups || [];
          this.totalHits = response.result.totalHits || 0;
          this.isOpen = true;
          this.activeIndex = this.getTotalItems() > 0 ? 0 : -1;
          return;
        }

        this.groups = [];
        this.totalHits = 0;
        this.isOpen = true;
        this.activeIndex = -1;
      });

    this.subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onInput(value: string): void {
    this.query = value;
    this.searchQuery$.next(value.trim());
  }

  clear(): void {
    this.query = '';
    this.resetResults();
  }

  open(): void {
    if (this.query.trim().length >= 2) {
      this.isOpen = true;
      if (this.activeIndex < 0 && this.getTotalItems() > 0) {
        this.activeIndex = 0;
      }
    }
  }

  onEnter(): void {
    if (!this.isOpen || this.getTotalItems() === 0) {
      return;
    }

    const selectedItem = this.getItemByFlatIndex(this.activeIndex >= 0 ? this.activeIndex : 0);
    if (selectedItem) {
      this.navigateTo(selectedItem.item);
    }
  }

  onArrowDown(event: KeyboardEvent): void {
    if (this.getTotalItems() === 0) {
      return;
    }

    event.preventDefault();
    this.isOpen = true;
    this.activeIndex = this.activeIndex < 0
      ? 0
      : (this.activeIndex + 1) % this.getTotalItems();
    this.scrollActiveIntoView();
  }

  onArrowUp(event: KeyboardEvent): void {
    if (this.getTotalItems() === 0) {
      return;
    }

    event.preventDefault();
    this.isOpen = true;
    this.activeIndex = this.activeIndex < 0
      ? this.getTotalItems() - 1
      : (this.activeIndex - 1 + this.getTotalItems()) % this.getTotalItems();
    this.scrollActiveIntoView();
  }

  onItemHover(groupIndex: number, itemIndex: number): void {
    this.activeIndex = this.getFlatIndex(groupIndex, itemIndex);
  }

  isItemActive(groupIndex: number, itemIndex: number): boolean {
    return this.activeIndex === this.getFlatIndex(groupIndex, itemIndex);
  }

  navigateTo(item: GlobalSearchResultItem): void {
    if (!item.route) {
      return;
    }

    this.isOpen = false;
    this.router.navigateByUrl(item.route);
  }

  trackByGroup(index: number, group: GlobalSearchGroup): string {
    return `${group.moduleKey}-${index}`;
  }

  trackByItem(index: number, item: GlobalSearchResultItem): string {
    return `${item.moduleKey}-${item.entityId}-${index}`;
  }

  translate(key: string): string {
    return this.translateService.instant(key);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isOpen = false;
    this.activeIndex = -1;
  }

  private resetResults(): void {
    this.totalHits = 0;
    this.groups = [];
    this.isOpen = false;
    this.loading = false;
    this.activeIndex = -1;
  }

  private getTotalItems(): number {
    return this.groups.reduce((sum, group) => sum + (group.items?.length || 0), 0);
  }

  getFlatIndex(groupIndex: number, itemIndex: number): number {
    let offset = 0;
    for (let i = 0; i < groupIndex; i++) {
      offset += this.groups[i]?.items?.length || 0;
    }

    return offset + itemIndex;
  }

  private getItemByFlatIndex(flatIndex: number): { item: GlobalSearchResultItem; groupIndex: number; itemIndex: number } | null {
    if (flatIndex < 0) {
      return null;
    }

    let cursor = 0;
    for (let groupIndex = 0; groupIndex < this.groups.length; groupIndex++) {
      const groupItems = this.groups[groupIndex]?.items || [];
      for (let itemIndex = 0; itemIndex < groupItems.length; itemIndex++) {
        if (cursor === flatIndex) {
          return { item: groupItems[itemIndex], groupIndex, itemIndex };
        }

        cursor++;
      }
    }

    return null;
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => {
      const active = this.elementRef.nativeElement.querySelector<HTMLElement>(
        `[data-search-index="${this.activeIndex}"]`
      );
      active?.scrollIntoView({ block: 'nearest' });
    });
  }
}
